/**
 * Single point of contact with the worker API.
 *
 * Everything goes through `api.fetch` which:
 *   - prepends /api/v1
 *   - sets credentials: "include" so the session cookie travels
 *   - parses JSON and surfaces the {error: {message,...}} envelope as a thrown
 *     `ApiError` that React Query can render
 */

import { CLIENT_ID } from "./clientId";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions extends RequestInit {
  json?: unknown;
}

export const api = {
  async fetch<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
    const headers = new Headers(opts.headers);
    // Identify this tab so live-sync can skip echoing our own writes back to us.
    headers.set("X-Keep-Client", CLIENT_ID);
    let body = opts.body;
    if (opts.json !== undefined) {
      headers.set("content-type", "application/json");
      body = JSON.stringify(opts.json);
    }
    const res = await fetch(`/api/v1${path}`, {
      ...opts,
      headers,
      body,
      credentials: "include",
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json: unknown = text ? safeParse(text) : null;
    if (!res.ok) {
      const err = (json as { error?: { message?: string; code?: string } })?.error;
      throw new ApiError(err?.message || `${res.status} ${res.statusText}`, res.status, err?.code);
    }
    return json as T;
  },

  get: <T = unknown>(path: string) => api.fetch<T>(path),
  post: <T = unknown>(path: string, body?: unknown) =>
    api.fetch<T>(path, { method: "POST", json: body }),
  patch: <T = unknown>(path: string, body?: unknown) =>
    api.fetch<T>(path, { method: "PATCH", json: body }),
  put: <T = unknown>(path: string, body?: unknown) =>
    api.fetch<T>(path, { method: "PUT", json: body }),
  delete: <T = unknown>(path: string) => api.fetch<T>(path, { method: "DELETE" }),
};

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}
